import { useUser } from "@clerk/clerk-react";
import { Close, ExpandMore, SearchOutlined } from "@mui/icons-material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import Switch from "@mui/material/Switch";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Typography from "@mui/material/Typography";
import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  buildImpactCampaignPickerHiddenLabel,
  buildImpactCampaignPickerListHeading,
  buildImpactCampaignPickerOnlyHiddenLabel,
  buildImpactCampaignPickerSelectAllLabel,
  buildImpactCampaignPickerSelectedLabel,
  buildImpactReportCampaignRedeemedLabel,
  IMPACT_CAMPAIGN_PICKER_APPLY_LABEL,
  IMPACT_CAMPAIGN_PICKER_CANCEL_LABEL,
  IMPACT_CAMPAIGN_PICKER_CAP_HINT,
  IMPACT_CAMPAIGN_PICKER_CLEAR_LABEL,
  IMPACT_CAMPAIGN_PICKER_CLEAR_SEARCH_LABEL,
  IMPACT_CAMPAIGN_PICKER_CLOSE_LABEL,
  IMPACT_CAMPAIGN_PICKER_EMPTY_TEXT,
  IMPACT_CAMPAIGN_PICKER_ERROR_TEXT,
  IMPACT_CAMPAIGN_PICKER_HIDE_UNREDEEMED_LABEL,
  IMPACT_CAMPAIGN_PICKER_LOAD_MORE_LABEL,
  IMPACT_CAMPAIGN_PICKER_LOADING_TEXT,
  IMPACT_CAMPAIGN_PICKER_NO_REDEMPTIONS_TAG,
  IMPACT_CAMPAIGN_PICKER_RECENT_HEADING,
  IMPACT_CAMPAIGN_PICKER_RETRY_LABEL,
  IMPACT_CAMPAIGN_PICKER_SEARCH_DEBOUNCE_MS,
  IMPACT_CAMPAIGN_PICKER_SEARCH_MAX_LENGTH,
  IMPACT_CAMPAIGN_PICKER_SEARCH_PLACEHOLDER,
  IMPACT_CAMPAIGN_PICKER_SHOW_HIDDEN_LABEL,
  IMPACT_CAMPAIGN_PICKER_SORT_LABEL,
  IMPACT_CAMPAIGN_PICKER_SORT_OPTIONS,
  IMPACT_CAMPAIGN_PICKER_STATUS_FILTER_LABEL,
  IMPACT_CAMPAIGN_PICKER_STATUS_LABELS,
  IMPACT_CAMPAIGN_PICKER_STATUS_OPTIONS,
  IMPACT_CAMPAIGN_PICKER_TITLE,
  IMPACT_CAMPAIGN_PICKER_TOO_MANY_HINT,
  IMPACT_CAMPAIGN_PICKER_TRUNCATED_HINT,
  IMPACT_REPORT_MAX_FILTER_IDS,
} from "@/constants/impactReport";
import {
  useCampaignOptionMatchIds,
  useCampaignOptionsByIds,
  useCampaignOptionsInfinite,
} from "@/hooks/useCampaignOptions";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import { useMediaQuery } from "@/hooks/useMediaQuery";
import type {
  CampaignOption,
  CampaignOptionsSort,
  CampaignOptionsStatusFilter,
} from "@/interfaces/campaing";
import type { ImpactCampaignPickerDialogProps } from "@/interfaces/impactReport";
import { formatDateLong } from "@/utils/date-format";
import {
  readImpactCampaignHideUnredeemed,
  rememberImpactCampaignHideUnredeemed,
} from "@/utils/impact-campaign-recents-helper";

const FULL_SCREEN_QUERY = "(max-width:600px)";
const DIALOG_HEIGHT = "min(720px, calc(100% - 64px))";
const ROW_MIN_HEIGHT_PX = 44;
/** Starts the next page slightly before the sentinel scrolls into view. */
const SENTINEL_ROOT_MARGIN = "0px 0px 160px 0px";

const SECTION_HEADING_SX = {
  px: 3,
  pt: 1.5,
  pb: 0.5,
  fontSize: "11px",
  fontWeight: 700,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
  color: "var(--color-graphite)",
} as const;

const HINT_SX = { px: 3, py: 0.5, fontSize: "12px", color: "var(--color-graphite)" } as const;

const TEXT_BUTTON_SX = { textTransform: "none", color: "var(--color-dust-ink)" } as const;

const NO_REDEMPTIONS_TAG_SX = {
  height: 18,
  fontSize: "11px",
  bgcolor: "var(--color-light-dust)",
  color: "var(--color-graphite)",
  border: 0,
  verticalAlign: "middle",
  "& .MuiChip-label": { px: 0.75 },
} as const;

const ROW_SX = {
  display: "flex",
  alignItems: "flex-start",
  gap: 0.5,
  width: "100%",
  minHeight: ROW_MIN_HEIGHT_PX,
  px: 3,
  py: "6px",
  boxSizing: "border-box",
  cursor: "pointer",
  "&:hover": { bgcolor: "action.hover" },
} as const;

function toSort(value: unknown): CampaignOptionsSort | undefined {
  return IMPACT_CAMPAIGN_PICKER_SORT_OPTIONS.find((option) => option.value === value)?.value;
}

function toStatusFilter(value: unknown): CampaignOptionsStatusFilter | undefined {
  return IMPACT_CAMPAIGN_PICKER_STATUS_OPTIONS.find((option) => option.value === value)?.value;
}

function pickerPrefsStorage(): Storage | undefined {
  return typeof window === "undefined" ? undefined : window.localStorage;
}

/** Per Clerk user; with no user the default (hide) applies and nothing is remembered. */
function readHideUnredeemed(userId: string | undefined): boolean {
  return userId === undefined || readImpactCampaignHideUnredeemed(pickerPrefsStorage(), userId);
}

/**
 * The impact report's campaign picker: search, sort and a status filter over the paged
 * `GET /campaigns/options` list, plus the user's recent campaigns, feeding a draft selection that
 * only leaves the dialog through Apply.
 */
export function ImpactCampaignPickerDialog(props: ImpactCampaignPickerDialogProps) {
  const { open, onClose, onExited } = props;
  const titleId = useId();
  const fullScreen = useMediaQuery(FULL_SCREEN_QUERY);

  // Each opening remounts the body, which is the only way to also reset the debounced search: its
  // trailing value lives inside `useDebouncedValue` and would otherwise query the old text once.
  const [session, setSession] = useState(0);
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) setSession((current) => current + 1);
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      maxWidth="sm"
      fullWidth
      fullScreen={fullScreen}
      disableRestoreFocus={onExited !== undefined}
      slotProps={{
        paper: { sx: { height: fullScreen ? undefined : DIALOG_HEIGHT } },
        transition: { onExited },
      }}
    >
      <ImpactCampaignPickerBody key={session} {...props} titleId={titleId} />
    </Dialog>
  );
}

function ImpactCampaignPickerBody({
  open,
  selectedCampaignIds,
  recentCampaignIds,
  recentCollapsed,
  onRecentCollapsedChange,
  onApply,
  onClose,
  titleId,
}: ImpactCampaignPickerDialogProps & { titleId: string }) {
  const [draft, setDraft] = useState<string[]>(selectedCampaignIds);
  const [search, setSearch] = useState("");
  const [sort, setSort] = useState<CampaignOptionsSort>("active");
  const [status, setStatus] = useState<CampaignOptionsStatusFilter>("all");
  const [capRefused, setCapRefused] = useState(false);
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  // Clerk may still be loading on the first render, so a later user id re-reads the preference.
  const { user } = useUser();
  const userId = user?.id;
  const [prefsUserId, setPrefsUserId] = useState(userId);
  const [hideUnredeemed, setHideUnredeemed] = useState(() => readHideUnredeemed(userId));
  if (userId !== prefsUserId) {
    setPrefsUserId(userId);
    setHideUnredeemed(readHideUnredeemed(userId));
  }

  function changeHideUnredeemed(hide: boolean) {
    setHideUnredeemed(hide);
    if (userId !== undefined) {
      rememberImpactCampaignHideUnredeemed(pickerPrefsStorage(), userId, hide);
    }
  }

  function clearSearch() {
    setSearch("");
    searchInputRef.current?.focus();
  }

  const debouncedSearch = useDebouncedValue(search, IMPACT_CAMPAIGN_PICKER_SEARCH_DEBOUNCE_MS);
  const querySearch = debouncedSearch.trim();
  const draftSet = useMemo(() => new Set(draft), [draft]);

  // The pinned-first set is a snapshot, re-taken only when the filter changes (and on reopen, via
  // the remount), so ticking a row never moves it. Re-taken during render rather than in an effect,
  // which would first fetch once with the stale pin.
  const filterKey = JSON.stringify([querySearch, sort, status, hideUnredeemed]);
  const [pinned, setPinned] = useState({ key: filterKey, ids: selectedCampaignIds });
  if (pinned.key !== filterKey) setPinned({ key: filterKey, ids: draft });

  const recentQuery = useCampaignOptionsByIds(recentCampaignIds);
  const recentRows = useMemo(() => {
    const byId = new Map((recentQuery.data ?? []).map((option) => [option.id, option]));
    return recentCampaignIds.flatMap((id) => byId.get(id) ?? []);
  }, [recentQuery.data, recentCampaignIds]);
  const showRecent = querySearch === "" && status === "all" && recentRows.length > 0;

  useEffect(() => {
    if (!recentQuery.isError) return;
    console.warn(
      "ImpactCampaignPickerDialog: recent campaigns could not be resolved; the Recent section is hidden",
      recentQuery.error,
    );
  }, [recentQuery.isError, recentQuery.error]);

  const listPin = pinned.key === filterKey ? pinned.ids : draft;
  const {
    data,
    error,
    isError,
    isPending,
    isPlaceholderData,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    refetch,
  } = useCampaignOptionsInfinite(
    { search: querySearch, sort, status, pin: listPin, hideUnredeemed },
    open,
  );

  const rows = useMemo(() => {
    // Offset paging over a list that can change between pages may repeat a row; keys must not.
    const seen = new Set<string>();
    return (data?.pages ?? [])
      .flatMap((page) => page.items)
      .filter((option) => {
        if (seen.has(option.id)) return false;
        seen.add(option.id);
        return true;
      });
  }, [data]);
  const total = data?.pages[0]?.total;
  const truncated = data?.pages.some((page) => page.truncated) ?? false;
  const hiddenCount = data?.pages[0]?.hiddenCount ?? 0;
  // Placeholder data carries the previous filter's count, so the notice waits for this one's.
  const isHidingAny = hideUnredeemed && hiddenCount > 0 && !isPlaceholderData;

  useEffect(() => {
    if (!isError) return;
    console.error("ImpactCampaignPickerDialog: campaign options failed to load", {
      search: querySearch,
      sort,
      status,
      hideUnredeemed,
      error,
    });
  }, [isError, error, querySearch, sort, status, hideUnredeemed]);

  // Placeholder rows belong to the previous filter, and an errored page would be retried on every
  // re-observe, so the observer only exists while the next page is safe to ask for.
  const canAutoLoad = open && hasNextPage && !isFetchingNextPage && !isError && !isPlaceholderData;
  useEffect(() => {
    if (!canAutoLoad || !sentinel) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) fetchNextPage();
      },
      { root: scrollRef.current, rootMargin: SENTINEL_ROOT_MARGIN },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [canAutoLoad, sentinel, fetchNextPage]);

  const recentHeadingId = useId();
  const recentListId = useId();
  const listHeadingId = useId();
  const statusLabelId = useId();
  const sortLabelId = useId();
  const tooManyHintId = useId();

  function commitDraft(next: string[]) {
    setDraft(next);
    setCapRefused(false);
  }

  function toggleCampaign(campaignId: string) {
    if (draftSet.has(campaignId)) {
      commitDraft(draft.filter((id) => id !== campaignId));
      return;
    }
    if (draft.length >= IMPACT_REPORT_MAX_FILTER_IDS) {
      setCapRefused(true);
      return;
    }
    commitDraft([...draft, campaignId]);
  }

  const isTooManyToSelectAll = total !== undefined && total > IMPACT_REPORT_MAX_FILTER_IDS;
  // Placeholder data still carries the previous filter's `total`, which must not decide whether
  // the new filter's ids fit under the cap.
  const {
    data: matchIds,
    error: matchIdsError,
    isError: isMatchIdsError,
    isFetching: isFetchingMatchIds,
    refetch: refetchMatchIds,
  } = useCampaignOptionMatchIds(
    { search: querySearch, status, pin: listPin, hideUnredeemed },
    open &&
      !isPlaceholderData &&
      total !== undefined &&
      total > 0 &&
      total <= IMPACT_REPORT_MAX_FILTER_IDS,
  );
  const matchIdsFailed = matchIds === undefined && isMatchIdsError;
  const isLoadingMatchIds = matchIds === undefined && isFetchingMatchIds;
  const selectAllCheckedCount = matchIds?.filter((id) => draftSet.has(id)).length ?? 0;
  const isAllSelected =
    matchIds !== undefined && matchIds.length > 0 && selectAllCheckedCount === matchIds.length;
  const canToggleSelectAll =
    !isTooManyToSelectAll &&
    !isPlaceholderData &&
    !isLoadingMatchIds &&
    (matchIds !== undefined || matchIdsFailed);

  useEffect(() => {
    if (!isMatchIdsError) return;
    console.error("ImpactCampaignPickerDialog: select-all could not fetch the matching ids", {
      search: querySearch,
      status,
      hideUnredeemed,
      error: matchIdsError,
    });
  }, [isMatchIdsError, matchIdsError, querySearch, status, hideUnredeemed]);

  function toggleSelectAll() {
    if (matchIds === undefined) {
      if (matchIdsFailed) refetchMatchIds();
      return;
    }
    const missing = matchIds.filter((id) => !draftSet.has(id));
    if (missing.length === 0) {
      const idSet = new Set(matchIds);
      commitDraft(draft.filter((id) => !idSet.has(id)));
      return;
    }
    // All or nothing: a partial add would be a silent truncation of what "Select all" promised.
    if (draft.length + missing.length > IMPACT_REPORT_MAX_FILTER_IDS) {
      setCapRefused(true);
      return;
    }
    commitDraft([...draft, ...missing]);
  }

  const showCapHint = capRefused || draft.length >= IMPACT_REPORT_MAX_FILTER_IDS;

  return (
    <>
      <DialogTitle
        id={titleId}
        sx={{
          fontFamily: "var(--font-crimson)",
          fontSize: 20,
          color: "var(--color-rodeo-black)",
          pr: 7,
        }}
      >
        {IMPACT_CAMPAIGN_PICKER_TITLE}
      </DialogTitle>
      {/* Outside the title, so the dialog's accessible name does not absorb "Close". */}
      <IconButton
        aria-label={IMPACT_CAMPAIGN_PICKER_CLOSE_LABEL}
        onClick={onClose}
        sx={{ position: "absolute", top: 10, right: 10, color: "var(--color-graphite)" }}
      >
        <Close />
      </IconButton>

      <Box sx={{ px: 3, pb: 2, display: "flex", flexDirection: "column", gap: 1.5 }}>
        <TextField
          autoFocus
          fullWidth
          size="small"
          inputRef={searchInputRef}
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder={IMPACT_CAMPAIGN_PICKER_SEARCH_PLACEHOLDER}
          slotProps={{
            htmlInput: {
              "aria-label": IMPACT_CAMPAIGN_PICKER_SEARCH_PLACEHOLDER,
              maxLength: IMPACT_CAMPAIGN_PICKER_SEARCH_MAX_LENGTH,
            },
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchOutlined sx={{ fontSize: 18, color: "var(--color-graphite)" }} />
                </InputAdornment>
              ),
              endAdornment: search !== "" && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    edge="end"
                    aria-label={IMPACT_CAMPAIGN_PICKER_CLEAR_SEARCH_LABEL}
                    onClick={clearSearch}
                    sx={{
                      color: "var(--color-graphite)",
                      "&:hover": { color: "var(--color-rodeo-black)" },
                      "&.Mui-focusVisible": {
                        outline: "2px solid var(--color-dust-ink)",
                        outlineOffset: "2px",
                      },
                    }}
                  >
                    <Close sx={{ fontSize: 18 }} />
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
        />
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 1.5,
          }}
        >
          <FormControl size="small" sx={{ minWidth: 170 }}>
            <InputLabel id={sortLabelId}>{IMPACT_CAMPAIGN_PICKER_SORT_LABEL}</InputLabel>
            <Select
              labelId={sortLabelId}
              label={IMPACT_CAMPAIGN_PICKER_SORT_LABEL}
              value={sort}
              onChange={(event) => setSort(toSort(event.target.value) ?? sort)}
              sx={{ fontSize: 13.5 }}
            >
              {IMPACT_CAMPAIGN_PICKER_SORT_OPTIONS.map((option) => (
                <MenuItem key={option.value} value={option.value} sx={{ fontSize: 13.5 }}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography
              id={statusLabelId}
              sx={{ fontSize: "12px", color: "var(--color-graphite)" }}
            >
              {IMPACT_CAMPAIGN_PICKER_STATUS_FILTER_LABEL}
            </Typography>
            <ToggleButtonGroup
              exclusive
              size="small"
              value={status}
              aria-labelledby={statusLabelId}
              // An exclusive group reports `null` when the pressed chip is clicked again; a status
              // filter always has a value, so that click is ignored.
              onChange={(_event, value) => {
                const next = toStatusFilter(value);
                if (next) setStatus(next);
              }}
            >
              {IMPACT_CAMPAIGN_PICKER_STATUS_OPTIONS.map((option) => (
                <ToggleButton
                  key={option.value}
                  value={option.value}
                  sx={{
                    px: 1.5,
                    fontSize: 12.5,
                    textTransform: "none",
                    color: "var(--color-graphite)",
                    "&.Mui-selected": {
                      bgcolor: "var(--color-light-dust)",
                      color: "var(--color-rodeo-black)",
                    },
                  }}
                >
                  {option.label}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
        </Box>
        <FormControlLabel
          control={
            <Switch
              size="small"
              checked={hideUnredeemed}
              onChange={(event) => changeHideUnredeemed(event.target.checked)}
            />
          }
          label={IMPACT_CAMPAIGN_PICKER_HIDE_UNREDEEMED_LABEL}
          slotProps={{
            typography: { sx: { fontSize: "13px", color: "var(--color-rodeo-black)" } },
          }}
          sx={{ alignSelf: "flex-start", ml: "-7px", mr: 0 }}
        />
      </Box>

      <DialogContent ref={scrollRef} dividers sx={{ px: 0, py: 1 }}>
        {showRecent && (
          <Box component="section" aria-labelledby={recentHeadingId} sx={{ pb: 1 }}>
            <Typography component="h3" id={recentHeadingId}>
              <ButtonBase
                aria-expanded={!recentCollapsed}
                aria-controls={recentListId}
                onClick={() => onRecentCollapsedChange(!recentCollapsed)}
                sx={{
                  ...SECTION_HEADING_SX,
                  fontFamily: "inherit",
                  lineHeight: "inherit",
                  width: "100%",
                  justifyContent: "flex-start",
                  gap: 0.5,
                  "&:hover": { bgcolor: "action.hover" },
                  "&.Mui-focusVisible": {
                    outline: "2px solid var(--color-primary-active)",
                    outlineOffset: "-2px",
                  },
                }}
              >
                {IMPACT_CAMPAIGN_PICKER_RECENT_HEADING}
                <ExpandMore
                  sx={{
                    fontSize: 16,
                    transform: recentCollapsed ? "rotate(-90deg)" : "none",
                    transition: "transform 120ms",
                  }}
                />
              </ButtonBase>
            </Typography>
            {/* Hidden rather than unmounted, so `aria-controls` always resolves. */}
            <List
              id={recentListId}
              hidden={recentCollapsed}
              disablePadding
              aria-labelledby={recentHeadingId}
            >
              {recentRows.map((option) => (
                <CampaignPickerRow
                  key={option.id}
                  option={option}
                  checked={draftSet.has(option.id)}
                  onToggle={toggleCampaign}
                />
              ))}
            </List>
          </Box>
        )}

        {/* An empty placeholder would state the previous filter's empty result, e.g. right after
            "Show them", so it reads as loading instead. */}
        {isPending || (isPlaceholderData && rows.length === 0) ? (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, px: 3, py: 2 }}>
            <CircularProgress size={18} sx={{ color: "var(--color-dark-dust)" }} />
            <Typography sx={{ fontSize: 13, color: "var(--color-graphite)" }}>
              {IMPACT_CAMPAIGN_PICKER_LOADING_TEXT}
            </Typography>
          </Box>
        ) : (
          <Box
            component="section"
            aria-labelledby={total === undefined ? undefined : listHeadingId}
          >
            {total !== undefined && (
              <Typography component="h3" id={listHeadingId} sx={SECTION_HEADING_SX}>
                {buildImpactCampaignPickerListHeading(total)}
              </Typography>
            )}
            {isHidingAny && rows.length > 0 && (
              <Box
                sx={{
                  ...HINT_SX,
                  display: "flex",
                  flexWrap: "wrap",
                  alignItems: "center",
                  gap: 0.5,
                }}
              >
                <span>{buildImpactCampaignPickerHiddenLabel(hiddenCount)} ·</span>
                <Button
                  size="small"
                  onClick={() => changeHideUnredeemed(false)}
                  sx={{ ...TEXT_BUTTON_SX, minWidth: 0, py: 0, fontSize: "12px" }}
                >
                  {IMPACT_CAMPAIGN_PICKER_SHOW_HIDDEN_LABEL}
                </Button>
              </Box>
            )}
            {truncated && (
              <Typography sx={HINT_SX}>{IMPACT_CAMPAIGN_PICKER_TRUNCATED_HINT}</Typography>
            )}

            {total !== undefined && total > 0 && (
              <Box sx={{ borderBottom: "1px solid var(--color-smoke)", pb: 0.5, mb: 0.5 }}>
                <Box
                  component="label"
                  sx={{
                    ...ROW_SX,
                    alignItems: "center",
                    cursor: isTooManyToSelectAll ? "default" : "pointer",
                  }}
                >
                  <Checkbox
                    size="small"
                    sx={{ p: 0.5, mr: 1 }}
                    checked={isAllSelected}
                    indeterminate={selectAllCheckedCount > 0 && !isAllSelected}
                    disabled={!canToggleSelectAll}
                    onChange={toggleSelectAll}
                    slotProps={{
                      input: {
                        "aria-describedby": isTooManyToSelectAll ? tooManyHintId : undefined,
                      },
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: "13.5px",
                      fontWeight: 700,
                      color: isTooManyToSelectAll
                        ? "var(--color-graphite)"
                        : "var(--color-rodeo-black)",
                    }}
                  >
                    {buildImpactCampaignPickerSelectAllLabel(total)}
                  </Typography>
                  {isLoadingMatchIds && (
                    <CircularProgress
                      size={14}
                      aria-label={IMPACT_CAMPAIGN_PICKER_LOADING_TEXT}
                      sx={{ ml: 1, color: "var(--color-dark-dust)" }}
                    />
                  )}
                </Box>
                {isTooManyToSelectAll && (
                  <Typography id={tooManyHintId} sx={HINT_SX}>
                    {IMPACT_CAMPAIGN_PICKER_TOO_MANY_HINT}
                  </Typography>
                )}
                {matchIdsFailed && (
                  <Typography role="alert" sx={{ ...HINT_SX, color: "error.main" }}>
                    {IMPACT_CAMPAIGN_PICKER_ERROR_TEXT}
                  </Typography>
                )}
              </Box>
            )}

            {rows.length > 0 ? (
              <List
                disablePadding
                aria-labelledby={total === undefined ? undefined : listHeadingId}
                sx={{ opacity: isPlaceholderData ? 0.6 : 1, transition: "opacity 120ms" }}
              >
                {rows.map((option) => (
                  <CampaignPickerRow
                    key={option.id}
                    option={option}
                    checked={draftSet.has(option.id)}
                    onToggle={toggleCampaign}
                  />
                ))}
              </List>
            ) : (
              !isError &&
              (isHidingAny ? (
                <Box sx={{ px: 3, py: 2 }}>
                  <Typography sx={{ fontSize: 13, color: "var(--color-graphite)", mb: 0.5 }}>
                    {buildImpactCampaignPickerOnlyHiddenLabel(hiddenCount)}
                  </Typography>
                  <Button
                    size="small"
                    onClick={() => changeHideUnredeemed(false)}
                    sx={TEXT_BUTTON_SX}
                  >
                    {IMPACT_CAMPAIGN_PICKER_SHOW_HIDDEN_LABEL}
                  </Button>
                </Box>
              ) : (
                <Typography sx={{ px: 3, py: 2, fontSize: 13, color: "var(--color-graphite)" }}>
                  {IMPACT_CAMPAIGN_PICKER_EMPTY_TEXT}
                </Typography>
              ))
            )}

            {hasNextPage && (
              <>
                <Box ref={setSentinel} aria-hidden sx={{ height: "1px" }} />
                <Box sx={{ px: 3, py: 1 }}>
                  <Button
                    size="small"
                    onClick={() => fetchNextPage()}
                    disabled={isFetchingNextPage}
                    startIcon={
                      isFetchingNextPage ? (
                        <CircularProgress size={14} sx={{ color: "var(--color-dark-dust)" }} />
                      ) : undefined
                    }
                    sx={{ textTransform: "none", color: "var(--color-dust-ink)" }}
                  >
                    {IMPACT_CAMPAIGN_PICKER_LOAD_MORE_LABEL}
                  </Button>
                </Box>
              </>
            )}

            {isError && (
              <Box role="alert" sx={{ px: 3, py: 2 }}>
                <Typography sx={{ fontSize: 13, color: "var(--color-rodeo-black)", mb: 1 }}>
                  {IMPACT_CAMPAIGN_PICKER_ERROR_TEXT}
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  onClick={() => refetch()}
                  sx={{ textTransform: "none" }}
                >
                  {IMPACT_CAMPAIGN_PICKER_RETRY_LABEL}
                </Button>
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      <Box aria-live="polite" sx={{ px: 3, pt: showCapHint ? 1 : 0 }}>
        {showCapHint && (
          <Typography sx={{ fontSize: "12px", color: "var(--color-rodeo-black)" }}>
            {IMPACT_CAMPAIGN_PICKER_CAP_HINT}
          </Typography>
        )}
      </Box>

      <DialogActions
        sx={{ px: 3, py: 1.5, justifyContent: "space-between", flexWrap: "wrap", gap: 1 }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Typography sx={{ fontSize: 13, color: "var(--color-rodeo-black)" }}>
            {buildImpactCampaignPickerSelectedLabel(draft.length)}
          </Typography>
          <Button
            size="small"
            onClick={() => commitDraft([])}
            disabled={draft.length === 0}
            sx={{ textTransform: "none", color: "var(--color-dust-ink)" }}
          >
            {IMPACT_CAMPAIGN_PICKER_CLEAR_LABEL}
          </Button>
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button onClick={onClose} sx={{ color: "var(--color-dust-ink)" }}>
            {IMPACT_CAMPAIGN_PICKER_CANCEL_LABEL}
          </Button>
          <Button
            variant="contained"
            onClick={() => onApply(draft)}
            sx={{ bgcolor: "var(--color-rodeo-black)" }}
          >
            {IMPACT_CAMPAIGN_PICKER_APPLY_LABEL}
          </Button>
        </Box>
      </DialogActions>
    </>
  );
}

/**
 * One campaign row, never disabled. At 0 redeemed a "No redemptions yet" tag takes the count's
 * place; the name keeps full contrast either way.
 */
function CampaignPickerRow({
  option,
  checked,
  onToggle,
}: {
  option: CampaignOption;
  checked: boolean;
  onToggle: (campaignId: string) => void;
}) {
  // Per row, not per campaign: the same campaign can sit in Recent and in the main list at once.
  const nameId = useId();
  const detailId = useId();

  return (
    <ListItem disablePadding>
      <Box component="label" sx={ROW_SX}>
        <Checkbox
          checked={checked}
          onChange={() => onToggle(option.id)}
          size="small"
          sx={{ p: 0.5, mr: 1 }}
          slotProps={{ input: { "aria-labelledby": nameId, "aria-describedby": detailId } }}
        />
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography id={nameId} sx={{ fontSize: "13.5px", color: "var(--color-rodeo-black)" }}>
            {option.name}
          </Typography>
          {/* A div, since the Chip's root is one and cannot sit inside a <p>. */}
          <Typography
            id={detailId}
            component="div"
            sx={{ fontSize: "11.5px", color: "var(--color-graphite)" }}
          >
            {IMPACT_CAMPAIGN_PICKER_STATUS_LABELS[option.status] ?? option.status} ·{" "}
            {option.redeemedCount === 0 ? (
              <Chip
                size="small"
                label={IMPACT_CAMPAIGN_PICKER_NO_REDEMPTIONS_TAG}
                sx={NO_REDEMPTIONS_TAG_SX}
              />
            ) : (
              <Box component="span" sx={{ color: "var(--color-rodeo-black)" }}>
                {buildImpactReportCampaignRedeemedLabel(option.redeemedCount)}
              </Box>
            )}{" "}
            · {formatDateLong(option.startDate)} – {formatDateLong(option.endDate)}
          </Typography>
        </Box>
      </Box>
    </ListItem>
  );
}
